import { FC, useMemo } from "react";
import { Button, ButtonVariant, Hide, Show } from "shared/components";
import { Checkbox } from "shared/components/forms/Checkbox";
import { useTemplateContext } from "../../provider";
import { ApplicantType } from "shared/types";
import { FeesComponent } from "../../slices/VerificationsSlice/components/FeesComponent";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import templatesApi from "../../../../../api/landlord/templates";
import { VerificationSchemaView } from "../../slices/types";
import { screeningServicesTabs } from "../../constant";
import { CheckComponent } from "../CheckComponent";
import { ApplicantRoleLabel, has } from "shared/utils";
import { uploadTemplateSignature } from "./utils/signature";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { onSaveCleanUp } from "../../helpers";
import { applyPremiumWarningsDraft } from "shared/app/PremiumWarningsReport/draft";
import { Template } from "types/landlord/verificationTemplate";

interface Props {
  onCancel: () => void;
  onSave?: (template: Template) => void;
}

export const LayoutFooter: FC<Props> = ({ onCancel, onSave: _onSave }) => {
  const {
    template,
    setTemplate,
    partnerType,
    premiumWarnings,
    readOnly,
    selectedVerificationSchema,
    setSelectedVerificationSchema
  } = useTemplateContext();
  const userPaid = Boolean(template?.[`${partnerType}_paid`]);

  const { canSave } = useTemplateContext();

  const newTemplate = useMemo(() => {
    if (template) {
      let normalizedTemplate = onSaveCleanUp.onlyNewIncomePresent(template);
      normalizedTemplate =
        onSaveCleanUp.filterOutHousingHistory(normalizedTemplate);

      // The premium warnings add-on is edited through the draft; fold it back
      // in so the fee preview, validation and save all carry it.
      return applyPremiumWarningsDraft(normalizedTemplate, premiumWarnings);
    }

    return template;
  }, [template, premiumWarnings]);

  const handleApplicantPaidChange = (v: boolean) => {
    if (!template) return;
    setTemplate({
      ...template,
      [`${partnerType}_paid`]: v,
      [`${partnerType}_fee`]: {
        ...template[`${partnerType}_fee`],
        cents: v ? template[`${partnerType}_fee`].cents || 6000 : 0
      }
    });
  };

  const hasBackButton =
    [
      VerificationSchemaView.CreditReport,
      VerificationSchemaView.DiscountCodes,
      VerificationSchemaView.AdverseActionGenericTemplate,
      VerificationSchemaView.AdverseActionWATemplate
    ].includes(selectedVerificationSchema) ||
    screeningServicesTabs.includes(selectedVerificationSchema);
  const hasCancelButton = !hasBackButton;

  const hasSaveButton = ![
    VerificationSchemaView.AdverseActionGenericTemplate,
    VerificationSchemaView.AdverseActionWATemplate
  ].includes(selectedVerificationSchema);

  const [debouncedTemplateParams] = useDebouncedValue(newTemplate, 500);

  const updateApplicationFeeCents = (newCents: number) => {
    if (!template) return;

    const updatedTemplate = {
      ...template,
      [`${partnerType}_fee`]: {
        ...template[`${partnerType}_fee`],
        cents: newCents
      }
    };
    setTemplate(updatedTemplate);
  };

  const { data: feeData } = useQuery({
    queryKey: ["fetchFeeData", debouncedTemplateParams],
    queryFn: () => {
      if (!debouncedTemplateParams) return Promise.resolve(null);

      return templatesApi.validateTemplate(debouncedTemplateParams);
    },
    placeholderData: keepPreviousData,
    enabled: Boolean(debouncedTemplateParams)
  });

  const handleBackClick = () => {
    const isAdverseActionTemplate = [
      VerificationSchemaView.AdverseActionGenericTemplate,
      VerificationSchemaView.AdverseActionWATemplate
    ].includes(selectedVerificationSchema);

    setSelectedVerificationSchema(
      isAdverseActionTemplate
        ? VerificationSchemaView.AdverseAction
        : VerificationSchemaView.ScreeningServices
    );
  };

  // This is a special formatting for save, but not for display
  const templateAdverseActionSignatures = (template: Template) => {
    if (
      has(template.adverse_action_letter_signature?.svg) &&
      typeof template.adverse_action_letter_signature.svg === "object" &&
      "value" in template.adverse_action_letter_signature.svg &&
      has(template.adverse_action_letter_signature.png) &&
      typeof template.adverse_action_letter_signature.png === "object" &&
      "value" in template.adverse_action_letter_signature.png
    )
      return {
        ...template,
        adverse_action_letter_signature: {
          svg: template.adverse_action_letter_signature.svg.value,
          png: template.adverse_action_letter_signature.png.value
        }
      };

    return template;
  };

  const onSave = async () => {
    if (!newTemplate) return;

    const gidTemplate = templateAdverseActionSignatures(newTemplate);
    const updatedTemplate = await uploadTemplateSignature(gidTemplate);
    if (updatedTemplate) {
      _onSave?.(updatedTemplate);
      setTemplate(newTemplate);
    }
  };

  return (
    <>
      <Hide
        if={
          [ApplicantType.CoSigner, ApplicantType.Occupant].includes(
            partnerType
          ) && !template?.settings[partnerType]?.enabled_screening_services
        }
      >
        {template &&
        selectedVerificationSchema ===
          VerificationSchemaView.ScreeningServices ? (
          <div className="px-6 py-4 border-t border-alpha-100">
            <div className="pb-2">
              {readOnly ? (
                <CheckComponent
                  checked={userPaid}
                  label={`${ApplicantRoleLabel[partnerType]} paid`}
                />
              ) : (
                <Checkbox
                  checked={userPaid}
                  label={`${ApplicantRoleLabel[partnerType]} paid`}
                  onChange={(e) => handleApplicantPaidChange(e.target.checked)}
                />
              )}
            </div>
            <FeesComponent
              feeData={
                feeData?.verification_template.costs?.[partnerType] ||
                // Visual hotfix; remove once the backend returns costs here.
                template.costs?.[partnerType]
              }
              onFeeChange={(newCents) => updateApplicationFeeCents(newCents)}
              readOnly={readOnly}
              isApplicantPaid={userPaid}
            />
          </div>
        ) : null}
      </Hide>
      <Hide if={readOnly}>
        <div className="flex items-center justify-between md:gap-4 gap-2 flex-wrap md:flex-nowrap py-4 px-6 border-t border-alpha-100">
          <div className="hidden lg:flex items-center gap-2.5">
            <HugeiconsIcon
              icon={InformationCircleIcon}
              className="text-content-100"
              size={20}
            />

            <p className="text-sm font-medium text-content-400">
              Changes will only affect new applications
            </p>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Show if={hasBackButton}>
              <Button
                variant={ButtonVariant.BASE}
                onClick={handleBackClick}
                className="w-[73px]"
              >
                Back
              </Button>
            </Show>

            <Show if={hasCancelButton}>
              <Button
                variant={ButtonVariant.BASE}
                onClick={onCancel}
                className="w-[138px]"
              >
                Cancel
              </Button>
            </Show>

            <Show if={hasSaveButton}>
              <Button
                variant={ButtonVariant.PRIMARY}
                onClick={onSave}
                className="w-[138px]"
                disabled={!canSave}
              >
                Save changes
              </Button>
            </Show>
          </div>
        </div>
      </Hide>
    </>
  );
};
